/// <amd-module name="TFS/Token/PatTokenPage/Components/PatPageHeader" />
import { PanelActionCreator, PanelOperationType } from 'Actions/PanelActionCreator';
import { TokenActionCreator } from 'Actions/TokenActionCreator';
import { IPanelContextValue, PanelConsumer } from 'Context/PanelContext';
import { IPatPageContextValue, PatPageConsumer } from 'Context/PatPageContext';
import { ITokenContextValue, TokenConsumer } from 'Context/TokenContext';
import { DisplayFilterOptions } from 'Contracts/Token';
import { TokenConfig } from 'Models/TokenConfig';
import * as Resources from "TFS/Token/PatTokenPage/Resources";
import { IOrganization, getUserOrganizations } from "VSS/Features/Services/OrganizationsService";
import { IVssPageContext } from "VSS/Platform/Context";
import { DropdownFilterBarItem } from 'VSSUI/Dropdown';
import { FilterBar } from "VSSUI/FilterBar";
import { Header, TitleSize } from 'VSSUI/Header';
import { ListSelection } from 'VSSUI/List';
import { IListBoxItem } from 'VSSUI/ListBox';
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { FILTER_RESET_EVENT, Filter } from "VSSUI/Utilities/Filter";
import * as React from 'react';

export interface IPatPageHeaderProps {
}

export interface IPatPageHeaderStates {
}

export interface IPatPageHeaderInternalProps extends IPatPageHeaderProps {
    tokenAction: TokenActionCreator;
    panelAction: PanelActionCreator;
    tokenConfig: TokenConfig;
    panelContext: IPanelContextValue;
    tokenContext: ITokenContextValue;
    pageContext: IVssPageContext;
}

export interface IPatPageHeaderInternalStates extends IPatPageHeaderStates {
    organizations: IOrganization[];
}

export interface IFilterData {
    filter: string,
    organization: IOrganization
}

export class PatPageHeaderInternal extends React.PureComponent<IPatPageHeaderInternalProps, IPatPageHeaderInternalStates> {
    private statusSelection = new ListSelection();
    private orgSelection = new ListSelection();
    private filter = new Filter();
    private currentSelectedStatus: IListBoxItem<{}>;
    private currentSelectedOrg: IOrganization;
    private isHosted: boolean;

    constructor(props: IPatPageHeaderInternalProps) {
        super(props);
        this.setCurrentOrganization();
        this.currentSelectedStatus = { id: "active" };
        this.isHosted = !!props.tokenConfig.hostPageData?.isHosted;

        this.filter.setDefaultState({
            "organizationsList": { value: { filter: this.currentSelectedOrg.id } },
            "statusList": { value: { filter: DisplayFilterOptions.Active } },
        });

        this.state = { organizations: [] };
        if (this.isHosted) {
            getUserOrganizations(props.pageContext).then(orgs => {
                this.setState({ organizations: orgs });
            });
        }
    }

    public componentDidMount(): void {
        this.filter.subscribe(this.onFilterReset, FILTER_RESET_EVENT);
    }

    public componentDidUpdate(): void {
        // Select the right dropdown list item index based on the filter enum item number
        // DisplayFilterOptions | ListSelection
        // 1 active               0 all
        // 2 revoked              1 active
        // 3 expired              2 revoked
        // 4 all                  3 expired
        this.statusSelection.select(this.props.tokenContext.selectedFilter.status % 4);

        const orgDropdownItems = this.getOrgDropdownItems();
        if (orgDropdownItems?.length) {
            const currentSelectedOrgIndex = orgDropdownItems.findIndex(org => org.id === this.currentSelectedOrg.id);
            this.orgSelection.select(currentSelectedOrgIndex);
        }
    }

    public render() {
        const orgDropdownItems = this.getOrgDropdownItems();

        return (
            <>
                {this.props.tokenContext.serverErrorMessage !== "" && (
                    <div>
                        <MessageBar severity={MessageBarSeverity.Error}>
                            <span className="text-word-break">{this.props.tokenContext.serverErrorMessage}</span>
                        </MessageBar>
                    </div>
                )}

                <Header
                    titleSize={TitleSize.Large}
                    title={Resources.PersonalAccessTokens}
                    description={Resources.PersonalAccessTokenDescription}
                    commandBarItems={[{
                        iconProps: { iconName: "Add" },
                        id: "newTokenCommand",
                        isPrimary: true,
                        text: Resources.NewToken,
                        important: true,
                        onActivate: this.openAddTokenDialog
                    }]}
                />

                <div className="page-content-left page-content-right page-content-top">
                    <FilterBar
                        filter={this.filter}
                        hideClearAction={false}
                    >

                        {orgDropdownItems && this.isHosted &&
                            <DropdownFilterBarItem
                                filterItemKey="organizationsList"
                                filter={this.filter}
                                items={orgDropdownItems}
                                selection={this.orgSelection}
                                onSelect={this.onOrgFilterSelect}
                                placeholder={Resources.Organization}
                                showPlaceholderAsLabel={true}
                            />
                        }

                        <DropdownFilterBarItem
                            filterItemKey="statusList"
                            filter={this.filter}
                            items={[
                                { id: "allTokens", text: Resources.AllTokens, data: { filter: DisplayFilterOptions.All } },
                                { id: "active", text: Resources.Active, data: { filter: DisplayFilterOptions.Active } },
                                { id: "revoked", text: Resources.Revoked, data: { filter: DisplayFilterOptions.Revoked } },
                                { id: "expired", text: Resources.Expired, data: { filter: DisplayFilterOptions.Expired } }
                            ]}
                            selection={this.statusSelection}
                            onSelect={this.onStatusFilterSelect}
                            placeholder={Resources.Status}
                            showPlaceholderAsLabel={true}
                        />

                    </FilterBar>
                </div>
            </>
        )
    }

    private onFilterReset = () => {
        this.setCurrentOrganization();
        this.currentSelectedStatus = { id: "active", text: Resources.Active, data: { filter: DisplayFilterOptions.Active } };
        this.props.tokenContext.onSelectedTokenChange(undefined);
        this.props.tokenAction.resetTokens({ status: DisplayFilterOptions.Active, organization: this.currentSelectedOrg });
    }

    private onStatusFilterSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {
        const keyboardEvent = event.type === "keydown" ? event as React.KeyboardEvent<HTMLElement> : undefined;

        if (event.type === "click" || keyboardEvent?.key === "Enter") {
            this.currentSelectedStatus = item;
            this.props.tokenContext.onSelectedTokenChange(undefined);
            this.queryResetTokens(item);
        }
    }

    private onOrgFilterSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IFilterData>) => {
        const keyboardEvent = event.type === "keydown" ? event as React.KeyboardEvent<HTMLElement> : undefined;

        if ((event.type === "click" || keyboardEvent?.key == "Enter") && item && item.data) {
            this.currentSelectedOrg = item.data.organization;
            this.props.tokenContext.onSelectedTokenChange(undefined);
            this.queryResetTokens(this.currentSelectedStatus);
        }
    }

    private queryResetTokens = (currentSelectedStatus: IListBoxItem<{}>) => {
        const { resetTokens } = this.props.tokenAction;

        switch (currentSelectedStatus.id) {
            case "allTokens":
                resetTokens({ status: DisplayFilterOptions.All, organization: this.currentSelectedOrg });
                break;
            case "active":
                resetTokens({ status: DisplayFilterOptions.Active, organization: this.currentSelectedOrg });
                break;
            case "revoked":
                resetTokens({ status: DisplayFilterOptions.Revoked, organization: this.currentSelectedOrg });
                break;
            case "expired":
                resetTokens({ status: DisplayFilterOptions.Expired, organization: this.currentSelectedOrg });
                break;
        }
    }

    private setCurrentOrganization = () => {
        const { hostPageData } = this.props.tokenConfig;
        this.currentSelectedOrg = {
            id: hostPageData ? hostPageData.hostId : "",
            name: hostPageData ? hostPageData.hostName : "",
            url: hostPageData ? window.location.origin + hostPageData.hostPath : "",
        };
    }

    private openAddTokenDialog = (): void => {
        this.props.panelAction.openTokenPanel(PanelOperationType.Add, undefined);
    }

    private getOrgDropdownItems(): IListBoxItem<IFilterData>[] | undefined {
        if (this.state.organizations) {
            return this.state.organizations.map(organization => {
                return {
                    id: organization.id, text: organization.name, data: { filter: organization.id, organization: organization }
                }
            });
        }
    }
}

export const PatPageHeader = (props: IPatPageHeaderProps) =>
    <PatPageConsumer>
        {(patPageContext: IPatPageContextValue) =>
            <PanelConsumer>
                {(panelContext: IPanelContextValue) =>
                    <TokenConsumer>
                        {(tokenContext: ITokenContextValue) =>
                            <PatPageHeaderInternal
                                panelContext={panelContext}
                                tokenContext={tokenContext}
                                tokenConfig={TokenConfig.getInstance(patPageContext.pageContext)}
                                panelAction={PanelActionCreator.getInstance()}
                                tokenAction={TokenActionCreator.getInstance(patPageContext.pageContext)}
                                pageContext={patPageContext.pageContext}
                            />}
                    </TokenConsumer>}
            </PanelConsumer>}
    </PatPageConsumer>
